import { useCallback, useEffect, useRef, useState } from 'react';
import { getPreviewLayers } from '../../../services/api';
import { needsChannel, serializeOverlays } from './editorModel';
import { createPreviewQueue } from './previewQueue';

export default function useLayerPreview({ sessionId, resolution, overlays, time, syncOffset, ready, videoTransform }) {
  const [preview, setPreview] = useState({ background: null, layers: {}, key: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const queueRef = useRef(null);
  const backgroundRef = useRef({ key: null, image: null });
  const key = JSON.stringify([sessionId, resolution, time, syncOffset, videoTransform]);

  useEffect(() => {
    backgroundRef.current = { key: null, image: null };
    const queue = createPreviewQueue({
      async render(job, signal) {
        const includeBackground = backgroundRef.current.key !== job.key;
        const response = await getPreviewLayers(sessionId, {
          ...job.payload, include_background: includeBackground,
        }, signal);
        if (!Array.isArray(response.layers) || response.layers.length !== job.overlays.length
          || !(response.width > 0) || !(response.height > 0)
          || (includeBackground && !response.background)) {
          throw new Error('The renderer returned an incomplete preview. Please retry.');
        }
        return response;
      },
      onResult(result, job) {
        if (result.background) {
          backgroundRef.current = { key: job.key, image: `data:image/png;base64,${result.background}` };
        }
        const layers = {};
        for (const layer of result.layers) {
          const overlay = job.overlays[layer.index];
          if (overlay) {
            layers[overlay._id] = {
              ...layer, image: layer.image ? `data:image/png;base64,${layer.image}` : null,
              position: overlay.position, frameWidth: result.width, frameHeight: result.height,
            };
          }
        }
        setPreview({ background: backgroundRef.current.image, layers, key: job.key });
        setError(null);
      },
      onError(e) { setError(e.message || 'Preview could not be updated. Please retry.'); },
      onBusy: setLoading,
    });
    queueRef.current = queue;
    return () => { queue.dispose(); queueRef.current = null; };
  }, [sessionId, resolution]);

  useEffect(() => {
    if (!ready || !Number.isFinite(syncOffset)) {
      queueRef.current.invalidate();
      setError(null);
      setPreview({ background: null, layers: {}, key: null });
      return;
    }
    const renderable = overlays.filter(o => !needsChannel(o));
    const payload = {
      overlays: serializeOverlays(renderable),
      resolution, csv_time_sec: time, sync_offset_sec: syncOffset, video_transform: videoTransform,
    };
    setError(null);
    queueRef.current.schedule({ key, overlays: renderable, payload });
  }, [key, overlays, ready, resolution, time, syncOffset, videoTransform, retry]);

  const refresh = useCallback(() => setRetry(n => n + 1), []);
  return { ...preview, loading, error, refresh, stale: preview.key !== key };
}
